import styled from 'styled-components';
import SvgIcon from '../SvgIcon/index';
import * as React from 'react';
import Button from '../Button/index';
import { Checkbox } from '../Checkbox/index';
import { ReactNode } from 'react';
import { BlueLink } from 'src/components/MPLink';
import PATHS from '../../constants/paths';
import { FormattedMessage } from 'react-intl';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  align-self: flex-start;
`;

const Descr = styled.span`
  line-height: 1.5;
  align-self: flex-start;
`;

const SmallDescr = styled(Descr)`
  line-height: 1.5;
  align-self: flex-start;
  font-size: ${p => p.theme.font.size.small};
  color: ${p => p.theme.color.black2};
`;

const ToDoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 5px 0;
`;

const ToDoTitle = styled.span`
  padding-left: 10px;
  font-size: ${p => p.theme.font.size.small};
  color: ${p => p.theme.color.black2};
`;

const ToDosDescr = styled.span`
  font-weight: ${p => p.theme.font.weight.medium};
  font-size: ${p => p.theme.font.size.small};
  color: ${p => p.theme.color.black2};
  align-self: flex-start;
`;

const ActionButton = styled(Button)`
  margin-top: 20px;
  width: 100%;
`;

const ToDo = (props: { isDone: boolean; title: ReactNode }) => {
  return (
    <ToDoContainer>
      <Checkbox isOn={props.isDone} />
      {props.title}
    </ToDoContainer>
  );
};

const ToDosListContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  align-self: flex-start;
`;

const ToDosContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
`;

interface InsightsInfoComponentProps {
  readonly isLoggedIn: boolean;
  readonly hasPages: boolean;
  readonly onCreateAccountClick: () => void;
  readonly onCreatePageClick: () => void;
  readonly onViewInsightsClick: () => void;
}

export const InsightsInfoComponent = (props: InsightsInfoComponentProps) => {
  const messageBase = 'components.InsightsInfo';

  const shouldShowToDos = !props.isLoggedIn || !props.hasPages;

  const createAccountLink = (): ReactNode => {
    return (
      <ToDoTitle>
        <FormattedMessage
          id={`${messageBase}.todo.account`}
          values={{
            create: (
              <BlueLink to={PATHS.AUTH.SIGN_UP}>
                <FormattedMessage id={`${messageBase}.todo.account.create`} />
              </BlueLink>
            ),
            login: (
              <BlueLink to={PATHS.AUTH.SIGN_IN}>
                <FormattedMessage id={`${messageBase}.todo.account.login`} />
              </BlueLink>
            ),
          }}
        />
      </ToDoTitle>
    );
  };

  const startWritingLink = (): ReactNode => {
    return (
      <ToDoTitle>
        <FormattedMessage
          id={`${messageBase}.todo.write`}
          values={{
            supported_languages: (
              <BlueLink to={PATHS.FAQ.LANGUAGE_SUPPORT} isExternal={true}>
                <FormattedMessage id={`${messageBase}.todo.write.supported_languages`} />
              </BlueLink>
            ),
          }}
        />
      </ToDoTitle>
    );
  };

  return (
    <Container>
      <SvgIcon iconWidth={200} iconHeight={190} iconName="img-insights" />
      <Title>
        <FormattedMessage id={`${messageBase}.title`} />
      </Title>

      <Descr>
        <FormattedMessage
          id={`${messageBase}.description`}
          values={{
            b: (msg: string) => <b>{msg}</b>,
            insights_faq_link: (
              <BlueLink to={PATHS.FAQ.INSIGHTS}>
                <FormattedMessage id={`${messageBase}.description.insights_faq_link`} />
              </BlueLink>
            ),
          }}
        />
      </Descr>
      <p />
      {shouldShowToDos ? (
        <ToDosContainer>
          <ToDosDescr>
            <FormattedMessage id={`${messageBase}.todos.title`} />
          </ToDosDescr>
          <ToDosListContainer>
            <ToDo isDone={props.isLoggedIn} title={createAccountLink()} />
            <ToDo isDone={props.hasPages} title={startWritingLink()} />
          </ToDosListContainer>
        </ToDosContainer>
      ) : (
        <div>
          <SmallDescr>
            <FormattedMessage id={`${messageBase}.empty_description`} />
          </SmallDescr>
          <SmallDescr>
            <FormattedMessage
              id={`${messageBase}.todo.write`}
              values={{
                supported_languages: (
                  <BlueLink to={PATHS.FAQ.LANGUAGE_SUPPORT} isExternal={true}>
                    <FormattedMessage id={`${messageBase}.todo.write.supported_languages`} />
                  </BlueLink>
                ),
              }}
            />
          </SmallDescr>
        </div>
      )}
      {!props.isLoggedIn ? (
        <ActionButton onClick={props.onCreateAccountClick}>
          <FormattedMessage id={`${messageBase}.action.create_account`} />
        </ActionButton>
      ) : !props.hasPages ? (
        <ActionButton onClick={props.onCreatePageClick}>
          <FormattedMessage id={`${messageBase}.action.start_writing`} />
        </ActionButton>
      ) : (
        <ActionButton onClick={props.onViewInsightsClick}>
          <FormattedMessage id={`${messageBase}.action.view_insights`} />
        </ActionButton>
      )}
    </Container>
  );
};



// WEBPACK FOOTER //
// ./src/components/InsightsInfoComponent/index.tsx